import * as Sentry from '@sentry/browser';
import * as singleSpa from 'single-spa';
import { getRuntimeConfig, loadRuntimeConfig } from './config/runtimeEnv';
import version from './config/version';
import * as logger from './logger';
import { setupIntercom } from './setupIntercom.js';
import { list as listChars } from './store/episodic-characters/episodic-character.api';
import { get as getEpisodic } from './store/episodics/episodic.api';
import { get as getPerms } from './store/permissions/permission.api';
import { ActionStatus } from './store/utils';

function renderConfigError(err: unknown) {
	document.body.innerHTML = `
		<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#333;">
			<h1 style="font-size:1.5rem;margin-bottom:0.5rem;">Service Unavailable</h1>
			<p style="color:#666;">Could not load application configuration. Please refresh or contact support.</p>
		</div>`;
	console.error('[bootstrap] Failed to load runtime config:', err);
}

import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
library.add(fas);

/**
 * Re-issues YII_CSRF_TOKEN after login.
 *
 * Login (callback.route.ts) clears the token for security, but in this SPA
 * architecture Yii never renders another page to issue a fresh one — so Angular's
 * first POST fires without a valid token and Yii rejects it with HTTP 400.
 *
 * The real auth guard is actionCsrf() in SiteController.php — it checks isGuest
 * and returns 401 if the Yii session is not authenticated, so the cookie is only
 * written when the user is genuinely logged in.
 */
async function reissueYiiCsrfToken(): Promise<void> {
	const cookies = document.cookie.split(';').map((c) => c.trim());
	if (!cookies.some((c) => c.startsWith('SOS_CSRF_TOKEN='))) return;
	if (cookies.some((c) => c.startsWith('YII_CSRF_TOKEN='))) return;

	try {
		const response = await fetch('/server/index.php?r=site/csrf', {
			credentials: 'include',
		});
		if (!response.ok) {
			logger.error(
				`[YiiCsrfToken] endpoint returned ${response.status} — YII_CSRF_TOKEN may not be set`
			);
		}
	} catch (err) {
		logger.error('[YiiCsrfToken] failed:', err);
	}
}

function searchURLIncludes(routes: string[]) {
	return routes.some((r) => window.location.search.includes(r));
}

function searchURLPathname(routes: string[]) {
	return routes.some((r) => window.location.pathname.startsWith(r));
}

loadRuntimeConfig().then(boot).catch(renderConfigError);

async function boot() {
	// store/index.ts and activity-monitor.saga.ts call getConfigValue at module
	// evaluation time — dynamic import ensures they're loaded AFTER config resolves.
	const [{ default: store }, storeActions] = await Promise.all([
		import('./store'),
		import('./store/actions'),
	]);

	const {
		accountActions,
		characterActions,
		episodicActions,
		episodicCharacterActions,
		permissionActions,
		userActions,
		userSettingActions,
	} = storeActions;

	const cfg = getRuntimeConfig();

	// Expose runtime config to Angular bundles (lazy-loaded after boot())
	// Angular files read this via (window.__SOS_CONFIG__ || {}).KEY
	(window as any).__SOS_CONFIG__ = cfg;

	if (logger.useSentry()) {
		Sentry.init({
			dsn: 'https://d4684564434443aebf5f30da4533d428@sentry.io/294655',
			environment: cfg.SOS_ENV,
			release: version,
		});
	}

	const appPathPrefixes = [
		'/navbar',
		'/angular',
		'/core',
		'/moneytracking',
		'/sales',
		'/app',
	];
	const skipUrls = [
		'?r=site/login',
		'?r=users/create',
		'?r=users/forgot',
		'?r=users/get',
		'?r=users/reset',
		'?r=users/verify',
		'?r=users/smartID',
		'?r=users/activate',
	];

	singleSpa.registerApplication(
		'navbar',
		() => import(/* webpackChunkName: "navbar" */ './navbar'),
		(location: Location) => {
			return (
				!location.pathname.startsWith('/studioadmin') &&
				!location.pathname.startsWith('/home') &&
				!location.pathname.startsWith('/app/users') &&
				!location.pathname.startsWith('/sales') &&
				location.pathname !== '' &&
				location.pathname !== '/' &&
				searchURLPathname(appPathPrefixes) &&
				!searchURLIncludes(skipUrls)
			);
		}
	);

	singleSpa.registerApplication(
		'core',
		() => import(/* webpackChunkName: "core" */ './core'),
		pathPrefix('/core')
	);
	singleSpa.registerApplication(
		'angular',
		() => import(/*webpackChunkName: "ng" */ 'sos-angular/synconset/spa'),
		pathPrefix('/angular')
	);
	singleSpa.registerApplication(
		'moneytracking',
		() => import(/*webpackChunkName: "mt" */ 'sos-angular/moneytracking/spa'),
		pathPrefix('/moneytracking')
	);
	singleSpa.registerApplication(
		'sales',
		() => import(/*webpackChunkName: "sales" */ 'sos-angular/sales/spa'),
		pathPrefix('/sales')
	);
	singleSpa.registerApplication(
		'studioadmin',
		() => import(/*webpackChunkName: "sa" */ 'sos-angular/studioadmin/spa'),
		pathPrefix('/studioadmin')
	);
	singleSpa.registerApplication(
		'app',
		() => import(/*webpackChunkName: "app"*/ './app'),
		pathPrefix('/app')
	);

	reissueYiiCsrfToken().then(() => {
		singleSpa.start();
	});

	function pathPrefix(prefix: string) {
		return function (location: Location) {
			return location.pathname.startsWith(`${prefix}`);
		};
	}

	window.addEventListener('single-spa:routing-event', async () => {
		if (location.pathname === '' || location.pathname === '/') {
			window.location.replace('/home');
		} else if (
			location.pathname === '/index.php' &&
			searchURLIncludes(skipUrls)
		) {
			window.location.replace(`/core${location.search}`);
		} else if (
			!location.pathname.startsWith('/app') &&
			!location.pathname.startsWith('/home') &&
			!searchURLIncludes(skipUrls)
		) {
			const { listed } = await getEpisodic();
			if (listed.Episodic) {
				store.dispatch(episodicActions.getComplete(listed.Episodic[0]));
				// need characters for the photo upload modal
				const charsRes = await listChars(listed.Episodic[0].id);
				if (charsRes.listed) {
					store.dispatch(
						episodicCharacterActions.listComplete(
							charsRes.listed.EpisodicCharacter
						)
					);
					store.dispatch(
						characterActions.listComplete(charsRes.listed.Character)
					);
				}
			}
			if (listed.Account) {
				store.dispatch(accountActions.listComplete(listed.Account));
			}
			await fetchPerms(listed.Episodic[0].id);
		} else if (location.pathname.startsWith('/app/revisions')) {
			// Handle /app/revisions - preserve existing episodic context
			// Get the current episodic from Redux state (the one user was already in)
			const state = store.getState();
			const currentEpisodic = state?.episodic?.entity;

			if (currentEpisodic && currentEpisodic.id) {
				// Just ensure permissions are loaded for the current episodic
				await fetchPerms(currentEpisodic.id);
			} else {
				// Fallback: if no episodic in Redux, fetch it
				const { listed } = await getEpisodic();
				if (listed.Episodic) {
					const episodic = listed.Episodic[0];
					store.dispatch(episodicActions.getComplete(episodic));
					// Fetch characters for the photo upload modal
					const charsRes = await listChars(episodic.id);
					if (charsRes.listed) {
						store.dispatch(
							episodicCharacterActions.listComplete(
								charsRes.listed.EpisodicCharacter
							)
						);
						store.dispatch(
							characterActions.listComplete(charsRes.listed.Character)
						);
					}
				}
				if (listed.Account) {
					store.dispatch(accountActions.listComplete(listed.Account));
				}
				// Fetch permissions
				if (listed.Episodic && listed.Episodic[0]) {
					await fetchPerms(listed.Episodic[0].id);
				}
			}
		} else if (location.pathname.startsWith('/app/episodics')) {
			const state = store.getState();
			if (state.episodics.getStatus === ActionStatus.Complete) {
				const currentId: number = state.episodics.currentId;
				await fetchPerms(currentId);
			}
		}
	});

	window.addEventListener('single-spa:no-app-change', () => {
		const appNames = singleSpa.getAppNames();
		const appLoaded = appNames.some((name) => {
			return singleSpa.getAppStatus(name) !== 'NOT_LOADED';
		});
		if (!appLoaded) {
			window.location.replace('/server/index.php?r=site/404');
		}
	});

	if (cfg.SOS_ENV !== 'local' || cfg.SOS_DEBUG === 'true') {
		// This starts up Pendo ΓÇö vendored snippet, cast to any to avoid TS noise
		(function (apiKey: string) {
			(function (p: any, e: any, n: any, d: any, o?: any) {
				let v: any, w: any, x: any, y: any, z: any;
				o = p[d] = p[d] || {};
				o._q = [];
				v = ['initialize', 'identify', 'updateOptions', 'pageLoad'];
				for (w = 0, x = v.length; w < x; ++w)
					(function (m: any) {
						o[m] =
							o[m] ||
							function () {
								o._q[m === v[0] ? 'unshift' : 'push'](
									[m].concat([].slice.call(arguments, 0))
								);
							};
					})(v[w]);
				y = e.createElement(n) as HTMLScriptElement;
				y.async = true;
				y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
				z = e.getElementsByTagName(n)[0];
				z.parentNode.insertBefore(y, z);
			})(window, document, 'script', 'pendo');
		})(cfg.PENDO_KEY);

		// This starts up Intercom
		setupIntercom(cfg);
	}

	async function fetchPerms(currentId?: number) {
		try {
			const result = await getPerms(currentId);
			if (result.listed.Permission) {
				store.dispatch(permissionActions.getComplete(result.listed.Permission));
			}
			if (result.listed.UserSetting) {
				store.dispatch(
					userSettingActions.getComplete(result.listed.UserSetting)
				);
			}
			if (result.listed.User) {
				store.dispatch(userActions.getComplete(result.listed.User[0]));
			}
		} catch (err: unknown) {
			store.dispatch(permissionActions.getFailed(err as Error));
			if ((err as any)?.response) {
				logger.log((err as any).response.data.message);
			} else {
				logger.error(err as Error);
			}
		}
	}
}
